import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { UiHelper } from '../helper/ui-helper';
import { LaunchNavigatorNative } from './launch-navigator-plugin';
import {
    AppSelectionOptions,
    extractCoordsFromLocationString,
    getAppDisplayName,
    isValidApp,
    isValidLaunchMode,
    isValidTransportMode,
    LAUNCH_NAVIGATOR_APP,
    LAUNCH_NAVIGATOR_LAUNCH_MODE,
    LAUNCH_NAVIGATOR_TRANSPORT_MODE,
    LaunchNavigatorOptions,
} from './launch-navigator.types';

const STORAGE_PREFIX = 'localforage/launchnavigator/';

const DEFAULT_APP_SELECTION_DIALOG_HEADER_TEXT = 'Navigations-App auswählen';
const DEFAULT_APP_SELECTION_CANCEL_BUTTON_TEXT = 'Abbrechen';

@Injectable({
    providedIn: 'root',
})
export class LaunchNavigatorService {
    readonly APP = LAUNCH_NAVIGATOR_APP;
    readonly LAUNCH_MODE = LAUNCH_NAVIGATOR_LAUNCH_MODE;
    readonly TRANSPORT_MODE = LAUNCH_NAVIGATOR_TRANSPORT_MODE;

    readonly appSelection = {
        userChoice: {
            exists: (): Promise<boolean> => this.storageItemExists('choice'),
            get: (): Promise<string | null> => this.getStorageItem('choice'),
            set: (app: string): Promise<void> => this.setStorageItem('choice', app),
            clear: (): Promise<void> => this.removeStorageItem('choice'),
        },
        userPrompted: {
            get: (): Promise<boolean> => this.storageItemExists('prompted'),
            set: (): Promise<void> => this.setStorageItem('prompted', 'true'),
            clear: (): Promise<void> => this.removeStorageItem('prompted'),
        },
    };

    private userSelectDisplayed = false;

    isAvailable(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    async setApiKey(apiKey: string): Promise<void> {
        if (!this.isAvailable()) {
            return;
        }

        await LaunchNavigatorNative.setApiKey({ apiKey });
    }

    async isAppAvailable(app: string): Promise<boolean> {
        if (!this.isAvailable()) {
            return false;
        }

        const result = await LaunchNavigatorNative.isAppAvailable({ app });
        return result.available;
    }

    async availableApps(): Promise<Record<string, boolean>> {
        if (!this.isAvailable()) {
            return {};
        }

        return LaunchNavigatorNative.availableApps();
    }

    async navigate(destination: string | number[], options: LaunchNavigatorOptions = {}): Promise<void> {
        if (!this.isAvailable()) {
            throw new Error('Navigation nicht verfügbar');
        }

        const resolvedOptions: LaunchNavigatorOptions = { ...options };
        resolvedOptions.app = resolvedOptions.app || LAUNCH_NAVIGATOR_APP.USER_SELECT;
        resolvedOptions.enableGeocoding = resolvedOptions.enableGeocoding !== false;

        if (resolvedOptions.app === LAUNCH_NAVIGATOR_APP.USER_SELECT) {
            return this.userSelect(destination, resolvedOptions);
        }

        if (!destination) {
            throw new Error('No destination was specified');
        }

        if (resolvedOptions.extras && typeof resolvedOptions.extras !== 'object') {
            throw new Error("'options.extras' must be a key/value object");
        }

        let dType: string;
        let sType = 'none';

        const normalizedDestination = extractCoordsFromLocationString(destination);
        if (typeof normalizedDestination === 'object') {
            if (typeof (normalizedDestination as number[]).length === 'undefined') {
                throw new Error('destination must be a string or an array');
            }

            dType = 'pos';
        } else {
            dType = 'name';
        }

        if (resolvedOptions.start) {
            const normalizedStart = extractCoordsFromLocationString(resolvedOptions.start);
            if (typeof normalizedStart === 'object') {
                if (typeof (normalizedStart as number[]).length === 'undefined') {
                    throw new Error('start must be a string or an array');
                }
                sType = 'pos';
            } else {
                sType = 'name';
            }

            resolvedOptions.start = normalizedStart as string | number[];
        }

        let transportMode: string | undefined;

        if (resolvedOptions.transportMode) {
            if (!isValidTransportMode(resolvedOptions.transportMode)) {
                throw new Error(`'${resolvedOptions.transportMode}' is not a recognised transport mode`);
            }
            transportMode = resolvedOptions.transportMode.charAt(0);
        }

        if (!resolvedOptions.app) {
            resolvedOptions.app = LAUNCH_NAVIGATOR_APP.GOOGLE_MAPS;
        }

        if (!isValidApp(resolvedOptions.app)) {
            throw new Error(`'${resolvedOptions.app}' is not a recognised app`);
        }

        if (!resolvedOptions.launchModeGoogleMaps) {
            resolvedOptions.launchModeGoogleMaps = LAUNCH_NAVIGATOR_LAUNCH_MODE.MAPS;
        }

        if (!isValidLaunchMode(resolvedOptions.launchModeGoogleMaps)) {
            throw new Error(`'${resolvedOptions.launchModeGoogleMaps}' is not a recognised launch mode`);
        }

        const dest = typeof normalizedDestination === 'object'
            ? JSON.stringify(normalizedDestination)
            : String(normalizedDestination);

        const start = resolvedOptions.start
            ? (typeof resolvedOptions.start === 'object'
                ? JSON.stringify(resolvedOptions.start)
                : String(resolvedOptions.start))
            : '';

        await LaunchNavigatorNative.navigate({
            app: resolvedOptions.app,
            dType,
            dest,
            destNickname: resolvedOptions.destinationName,
            sType,
            start,
            startNickname: resolvedOptions.startName,
            transportMode,
            launchMode: resolvedOptions.launchModeGoogleMaps,
            extras: resolvedOptions.extras ? JSON.stringify(resolvedOptions.extras) : undefined,
            enableGeocoding: resolvedOptions.enableGeocoding,
            latitude: options.latitude,
            longitude: options.longitude,
            lkz: options.lkz,
            ort: options.ort,
            plz: options.plz,
            strasse: options.strasse,
            hausnummer: options.hausnummer,
            geoKoordinatenBevorzugen: options.geoKoordinatenBevorzugen,
        });

        resolvedOptions.successCallback?.();
    }

    async userSelect(destination: string | number[], options: LaunchNavigatorOptions = {}): Promise<void> {
        if (!this.isAvailable()) {
            throw new Error('Navigation nicht verfügbar');
        }

        const resolvedOptions: LaunchNavigatorOptions = { ...options };
        const appSelection: AppSelectionOptions = resolvedOptions.appSelection || {};
        const rememberChoice = appSelection.rememberChoice || {};
        const rememberEnabled = rememberChoice.enabled ?? 'prompt';

        if (this.userSelectDisplayed) {
            return;
        }

        const apps = await this.availableApps();
        const availableApps: { app: string; label: string }[] = [];

        for (const app of Object.keys(apps)) {
            if (!apps[app]) {
                continue;
            }
            if (appSelection.list?.length && !appSelection.list.includes(app)) {
                continue;
            }
            availableApps.push({
                app,
                label: getAppDisplayName(app),
            });
        }

        if (availableApps.length === 0) {
            const error = 'Keine unterstützte Navigations-App auf dem Gerät verfügbar';
            resolvedOptions.errorCallback?.(error);
            throw new Error(error);
        }

        const launchApp = async (app: string) => {
            resolvedOptions.app = app;
            await this.navigate(destination, resolvedOptions);
        };

        const rememberUserChoiceAndLaunch = async (app: string) => {
            await this.appSelection.userChoice.set(app);
            await launchApp(app);
        };

        const promptUser = async (app: string) => {
            const handleUserPromptChoice = async (shouldRemember: boolean) => {
                rememberChoice.prompt?.callback?.(shouldRemember);
                await this.appSelection.userPrompted.set();
                if (shouldRemember) {
                    await rememberUserChoiceAndLaunch(app);
                } else {
                    await launchApp(app);
                }
            };

            if (rememberChoice.promptFn) {
                rememberChoice.promptFn(handleUserPromptChoice);
            } else {
                const shouldRemember = await UiHelper.confirmJaNein(
                    rememberChoice.prompt?.bodyText || 'Gleiche App beim nächsten Mal verwenden?',
                    rememberChoice.prompt?.headerText || 'Auswahl merken?'
                );
                await handleUserPromptChoice(shouldRemember);
            }
        };

        const onChooseApp = async (app: string) => {
            this.userSelectDisplayed = false;
            appSelection.callback?.(app);

            if (rememberEnabled === true || rememberEnabled === 'true') {
                await rememberUserChoiceAndLaunch(app);
            } else if (rememberEnabled === false || rememberEnabled === 'false') {
                await launchApp(app);
            } else {
                const prompted = await this.appSelection.userPrompted.get();
                if (prompted) {
                    await launchApp(app);
                } else {
                    await promptUser(app);
                }
            }
        };

        const displayChooser = async () => {
            this.userSelectDisplayed = true;

            if (availableApps.length === 1) {
                await onChooseApp(availableApps[0].app);
                return;
            }

            const savedChoice = await this.appSelection.userChoice.get();
            if (savedChoice) {
                if (apps[savedChoice]) {
                    await onChooseApp(savedChoice);
                    return;
                }

                await this.appSelection.userChoice.clear();
                await this.appSelection.userPrompted.clear();
            }

            const actionSheet = await UiHelper.actionSheetController.create({
                header: appSelection.dialogHeaderText || DEFAULT_APP_SELECTION_DIALOG_HEADER_TEXT,
                buttons: [
                    ...availableApps.map(entry => ({
                        text: entry.label,
                        handler: () => {
                            void onChooseApp(entry.app);
                        },
                    })),
                    {
                        text: appSelection.cancelButtonText || DEFAULT_APP_SELECTION_CANCEL_BUTTON_TEXT,
                        role: 'cancel',
                        handler: () => {
                            this.userSelectDisplayed = false;
                            resolvedOptions.errorCallback?.('cancelled');
                        },
                    },
                ],
            });

            await actionSheet.present();
        };

        await displayChooser();
    }

    private async getStorageItem(key: string): Promise<string | null> {
        return localStorage.getItem(STORAGE_PREFIX + key);
    }

    private async setStorageItem(key: string, value: string): Promise<void> {
        localStorage.setItem(STORAGE_PREFIX + key, value);
    }

    private async removeStorageItem(key: string): Promise<void> {
        localStorage.removeItem(STORAGE_PREFIX + key);
    }

    private async storageItemExists(key: string): Promise<boolean> {
        return localStorage.getItem(STORAGE_PREFIX + key) !== null;
    }
}
